// ===========================================
// Notifications API Functions
// ===========================================
// API calls for notification preferences endpoints
// Supports email, SMS, and WhatsApp notifications

import { apiClient } from '../services/api';

// ===========================================
// TYPES
// ===========================================

export type NotificationType =
  | 'LESSON_REMINDER'
  | 'LESSON_RESCHEDULED'
  // Exists in the Prisma enum with a backend default of ['EMAIL','SMS'], but was
  // missing here until 2026-08-25 — so the backend gated cancellation emails on a
  // preference parents could neither see nor change.
  | 'LESSON_CANCELLED'
  | 'PAYMENT_RECEIVED'
  | 'INVOICE_CREATED'
  | 'HYBRID_BOOKING_OPENED'
  | 'HYBRID_BOOKING_REMINDER'
  | 'FILE_UPLOADED'
  | 'MESSAGE_RECEIVED'
  | 'ATTENDANCE_SUMMARY'
  | 'ABSENCE_CONFIRMED'
  | 'SUBSCRIPTION_RENEWAL'
  | 'SUBSCRIPTION_PAYMENT_FAILED';

export type NotificationChannel = 'EMAIL' | 'SMS' | 'WHATSAPP' | 'PUSH';

export type NotificationDeliveryStatus =
  | 'PENDING'
  | 'QUEUED'
  | 'SENT'
  | 'DELIVERED'
  | 'FAILED'
  | 'BOUNCED';

export interface NotificationPreference {
  id: string;
  userId: string;
  schoolId: string;
  // Legacy field (kept for backwards compatibility)
  emailNotificationsEnabled?: boolean;
  notificationTypes: Record<NotificationType, boolean> | Record<string, NotificationChannel[]>;
  quietHoursEnabled: boolean;
  quietHoursStart: string | null;
  quietHoursEnd: string | null;
  quietHoursTimezone: string | null;
  // Multi-channel fields
  emailEnabled: boolean;
  smsEnabled: boolean;
  whatsappEnabled: boolean;
  phoneNumber: string | null;
  phoneVerified: boolean;
  phoneVerifiedAt: string | null;
  whatsappOptedIn: boolean;
  whatsappOptedInAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationTypeSettings {
  [key: string]: NotificationChannel[];
}

export interface ExtendedPreferencesResponse {
  preferences: NotificationPreference;
  typeSettings: NotificationTypeSettings;
}

export interface UpdatePreferencesInput {
  // Legacy fields
  emailNotificationsEnabled?: boolean;
  notificationTypes?: Partial<Record<NotificationType, boolean>>;
  // New multi-channel fields
  emailEnabled?: boolean;
  smsEnabled?: boolean;
  whatsappEnabled?: boolean;
  phoneNumber?: string | null;
  quietHoursEnabled?: boolean;
  quietHoursStart?: string | null;
  quietHoursEnd?: string | null;
  quietHoursTimezone?: string | null;
}

export interface UpdatePhoneInput {
  phoneNumber: string;
}

export interface UpdateTypeSettingsInput {
  notificationType: NotificationType;
  channels: NotificationChannel[];
}

export interface VerifyPhoneCodeInput {
  code: string;
}

export interface SendCodeResponse {
  sent: boolean;
}

export interface SendTestMessageInput {
  phoneNumber?: string;
  message: string;
}

export interface SendTestMessageResult {
  messageId?: string;
  notificationLogId?: string;
}

export interface NotificationLog {
  id: string;
  notificationType: NotificationType;
  channel: NotificationChannel;
  recipientPhone: string | null;
  recipientEmail: string | null;
  status: NotificationDeliveryStatus;
  contentPreview: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  failedAt: string | null;
  providerError: string | null;
  createdAt: string;
}

export interface NotificationDeliveryStats {
  total: number;
  byChannel: Record<string, number>;
  byStatus: Record<string, number>;
  successRate: number;
}

export interface NotificationServiceStatus {
  twilioEnabled: boolean;
  smsEnabled: boolean;
  whatsappEnabled: boolean;
  whatsappProvider?: string;
}

// ===========================================
// NOTIFICATION TYPE LABELS
// ===========================================

export const notificationTypeLabels: Record<NotificationType, string> = {
  LESSON_REMINDER: 'Lesson Reminders',
  LESSON_RESCHEDULED: 'Lesson Rescheduled',
  LESSON_CANCELLED: 'Lesson Cancelled',
  PAYMENT_RECEIVED: 'Payment Confirmations',
  INVOICE_CREATED: 'New Invoices',
  HYBRID_BOOKING_OPENED: 'Hybrid Booking Opens',
  HYBRID_BOOKING_REMINDER: 'Hybrid Booking Reminders',
  FILE_UPLOADED: 'New File Uploads',
  MESSAGE_RECEIVED: 'Message Notifications',
  ATTENDANCE_SUMMARY: 'Weekly Attendance Summary',
  ABSENCE_CONFIRMED: 'Absence Confirmations',
  SUBSCRIPTION_RENEWAL: 'Subscription Renewals',
  SUBSCRIPTION_PAYMENT_FAILED: 'Payment Failures',
};

export const notificationTypeDescriptions: Record<NotificationType, string> = {
  LESSON_REMINDER: 'Receive reminders before upcoming lessons',
  LESSON_RESCHEDULED: 'Get notified when a lesson time changes',
  LESSON_CANCELLED: 'Get notified when a class, 1:1 session or extra class is cancelled',
  PAYMENT_RECEIVED: 'Confirmation emails for successful payments',
  INVOICE_CREATED: 'Notifications when new invoices are created',
  HYBRID_BOOKING_OPENED: 'Alert when hybrid lesson booking opens',
  HYBRID_BOOKING_REMINDER: 'Reminder to book hybrid individual sessions',
  FILE_UPLOADED: 'Notifications when teachers upload new resources',
  MESSAGE_RECEIVED: 'Notifications when you receive new messages',
  ATTENDANCE_SUMMARY: 'Weekly summary of your child\'s attendance',
  ABSENCE_CONFIRMED: 'Confirmation when an absence is reported',
  SUBSCRIPTION_RENEWAL: 'Reminder before subscription renewal',
  SUBSCRIPTION_PAYMENT_FAILED: 'Alert when a payment fails',
};

// ===========================================
// TEACHER-SPECIFIC NOTIFICATION LABELS
// ===========================================

export const teacherNotificationTypeLabels: Partial<Record<NotificationType, string>> = {
  LESSON_REMINDER: 'Lesson Reminders',
  LESSON_RESCHEDULED: 'Lesson Rescheduled',
  FILE_UPLOADED: 'File Uploads',
  MESSAGE_RECEIVED: 'Message Notifications',
  ATTENDANCE_SUMMARY: 'Weekly Attendance Summary',
};

export const teacherNotificationTypeDescriptions: Partial<Record<NotificationType, string>> = {
  LESSON_REMINDER: 'Receive reminders before lessons you teach',
  LESSON_RESCHEDULED: 'Get notified when a lesson you teach is rescheduled',
  FILE_UPLOADED: 'Notifications when files are shared with you',
  MESSAGE_RECEIVED: 'Notifications when you receive new messages',
  ATTENDANCE_SUMMARY: 'Weekly summary of attendance across your classes',
};

export const teacherNotificationTypeOrder: NotificationType[] = [
  'LESSON_REMINDER',
  'LESSON_RESCHEDULED',
  'FILE_UPLOADED',
  'MESSAGE_RECEIVED',
  'ATTENDANCE_SUMMARY',
];

export const notificationChannelLabels: Record<NotificationChannel, string> = {
  EMAIL: 'Email',
  SMS: 'SMS',
  WHATSAPP: 'WhatsApp',
  PUSH: 'Push',
};

// ===========================================
// NOTIFICATIONS API
// ===========================================

export const notificationsApi = {
  // =========================================
  // BASIC PREFERENCES (Legacy API)
  // =========================================

  // Get current user's notification preferences
  getPreferences: (): Promise<NotificationPreference> =>
    apiClient
      .get<{ status: string; data: NotificationPreference }>('/notifications/preferences')
      .then((res) => res.data),

  // Update notification preferences
  updatePreferences: (data: UpdatePreferencesInput): Promise<NotificationPreference> =>
    apiClient
      .patch<{ status: string; data: NotificationPreference }>(
        '/notifications/preferences',
        data
      )
      .then((res) => res.data),

  // Reset preferences to defaults
  resetToDefaults: (): Promise<NotificationPreference> =>
    apiClient
      .post<{ status: string; data: NotificationPreference }>(
        '/notifications/preferences/reset'
      )
      .then((res) => res.data),

  // =========================================
  // EXTENDED PREFERENCES (Multi-channel)
  // =========================================

  // Get extended preferences with type settings
  getExtendedPreferences: (): Promise<ExtendedPreferencesResponse> =>
    apiClient
      .get<{ status: string; data: ExtendedPreferencesResponse }>(
        '/notifications/preferences/extended'
      )
      .then((res) => res.data),

  // Update extended preferences (including SMS/WhatsApp)
  updateExtendedPreferences: (data: UpdatePreferencesInput): Promise<NotificationPreference> =>
    apiClient
      .patch<{ status: string; data: NotificationPreference }>(
        '/notifications/preferences/extended',
        data
      )
      .then((res) => res.data),

  // =========================================
  // PHONE NUMBER MANAGEMENT
  // =========================================

  // Update phone number
  updatePhone: (data: UpdatePhoneInput): Promise<NotificationPreference> =>
    apiClient
      .patch<{ status: string; data: NotificationPreference }>('/notifications/phone', data)
      .then((res) => res.data),

  // Mark phone as verified (legacy)
  verifyPhone: (): Promise<NotificationPreference> =>
    apiClient
      .post<{ status: string; data: NotificationPreference }>('/notifications/phone/verify')
      .then((res) => res.data),

  // Send verification code via SMS
  sendVerificationCode: (): Promise<SendCodeResponse> =>
    apiClient
      .post<{ status: string; data: SendCodeResponse }>('/notifications/phone/send-code')
      .then((res) => res.data),

  // Verify phone with 6-digit code
  verifyPhoneCode: (data: VerifyPhoneCodeInput): Promise<NotificationPreference> =>
    apiClient
      .post<{ status: string; data: NotificationPreference }>(
        '/notifications/phone/verify-code',
        data
      )
      .then((res) => res.data),

  // =========================================
  // WHATSAPP OPT-IN
  // =========================================

  // Record WhatsApp opt-in
  optInWhatsApp: (): Promise<NotificationPreference> =>
    apiClient
      .post<{ status: string; data: NotificationPreference }>('/notifications/whatsapp/opt-in')
      .then((res) => res.data),

  // =========================================
  // NOTIFICATION TYPE SETTINGS
  // =========================================

  // Update channels for a specific notification type
  updateTypeSettings: (data: UpdateTypeSettingsInput): Promise<NotificationPreference> =>
    apiClient
      .patch<{ status: string; data: NotificationPreference }>(
        '/notifications/type-settings',
        data
      )
      .then((res) => res.data),

  // Update all notification type settings at once
  updateAllTypeSettings: (settings: NotificationTypeSettings): Promise<NotificationPreference> =>
    apiClient
      .put<{ status: string; data: NotificationPreference }>(
        '/notifications/type-settings',
        settings
      )
      .then((res) => res.data),

  // =========================================
  // TEST MESSAGES
  // =========================================

  // Send test SMS
  sendTestSms: (data: SendTestMessageInput): Promise<SendTestMessageResult> =>
    apiClient
      .post<{ status: string; data: SendTestMessageResult }>('/notifications/test/sms', data)
      .then((res) => res.data),

  // Send test WhatsApp message
  sendTestWhatsApp: (data: SendTestMessageInput): Promise<SendTestMessageResult> =>
    apiClient
      .post<{ status: string; data: SendTestMessageResult }>(
        '/notifications/test/whatsapp',
        data
      )
      .then((res) => res.data),

  // =========================================
  // SERVICE STATUS
  // =========================================

  // Get SMS/WhatsApp service availability status
  getServiceStatus: (): Promise<NotificationServiceStatus> =>
    apiClient
      .get<{ status: string; data: NotificationServiceStatus }>('/notifications/status')
      .then((res) => res.data),

  // =========================================
  // ADMIN: NOTIFICATION LOGS (Admin only)
  // =========================================

  // Get notification delivery logs
  getLogs: (params?: {
    limit?: number;
    channel?: NotificationChannel;
    userId?: string;
  }): Promise<NotificationLog[]> =>
    apiClient
      .get<{ status: string; data: NotificationLog[] }>('/notifications/logs', { params })
      .then((res) => res.data),

  // Get delivery statistics
  getDeliveryStats: (params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<NotificationDeliveryStats> =>
    apiClient
      .get<{ status: string; data: NotificationDeliveryStats }>('/notifications/stats', {
        params,
      })
      .then((res) => res.data),
};
